import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";
import PlayerDemo from "./components/PlayerDemo";
import usePageMeta from "@/hooks/usePageMeta";
import { formatPopularity, getMovieById, getRelatedMovies } from "@/utils/movie";

export default function DetailPage() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const [favorited, setFavorited] = useState(false);

  usePageMeta({
    title: movie
      ? `${movie.title} - 激情丛林在线播放 - 高清完整版`
      : "影视详情 - 激情丛林高清在线观看",
    description: movie
      ? `${movie.title}（${movie.year}·${movie.region}）由${movie.director}执导，${movie.actors.join("、")}主演。${movie.intro}`
      : "激情丛林影视详情页，提供高清完整版在线观看入口与剧情介绍。",
    keywords: movie
      ? `${movie.title},${movie.title}在线观看,激情丛林,${movie.genres.join(",")},高清影视`
      : "激情丛林,影视详情,在线观看",
  });

  useEffect(() => {
    setFavorited(false);
  }, [id]);

  if (!movie) {
    return (
      <div className="px-4 md:px-6 py-20">
        <div className="max-w-7xl mx-auto text-center bg-background-50 rounded-lg border border-background-200 py-20">
          <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
            <i className="ri-film-line text-3xl" aria-hidden="true"></i>
          </span>
          <p className="text-foreground-800 font-heading text-xl font-bold">该影视内容暂未上线</p>
          <p className="mt-2 text-sm text-foreground-400">
            您访问的影视资源不存在或已下架，可以返回首页浏览更多内容
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold px-5 py-2.5 rounded-md cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-home-4-line" aria-hidden="true"></i>
              返回首页
            </Link>
            <Link
              to="/search"
              className="inline-flex items-center gap-2 border border-background-300 hover:border-primary-400 text-foreground-700 text-sm font-semibold px-5 py-2.5 rounded-md cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-search-line" aria-hidden="true"></i>
              去搜索
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const categoryKey = movie.type === "电影" ? "movie" : "tv";
  const related = getRelatedMovies(movie, 4);

  const metaItems = [
    { label: "类别", value: movie.type },
    { label: "地区", value: movie.region },
    { label: "上映年份", value: movie.year },
    { label: "时长", value: movie.episodes ? `${movie.duration} · ${movie.episodes}` : movie.duration },
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.actors.join("、") },
  ];

  return (
    <article>
      <section className="relative w-full overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img
            src={movie.backdrop}
            alt={`${movie.title}剧照`}
            title={`${movie.title} 激情丛林在线观看`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/70 to-foreground-950/40"></div>
        </div>

        <div className="relative px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="[&_*]:!text-background-200">
              <Breadcrumb
                items={[
                  { label: "首页", to: "/" },
                  { label: movie.type, to: `/category/${categoryKey}` },
                  { label: movie.title },
                ]}
              />
            </div>

            <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="w-36 sm:w-44 md:w-52 shrink-0 mx-auto md:mx-0">
                <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={movie.cover}
                    alt={`${movie.title}海报`}
                    title={`${movie.title} ${movie.type}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
                  <span className="bg-accent-500 text-background-50 text-xs font-bold px-2.5 py-1 rounded-md">
                    {movie.type}
                  </span>
                  <span className="text-background-100 text-sm font-medium">
                    {movie.year} · {movie.region}
                  </span>
                </div>
                <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-background-50 mb-4">
                  {movie.title}
                </h1>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-5 gap-y-2 mb-5 text-sm text-background-100">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-400" aria-hidden="true"></i>
                    <span className="font-bold text-background-50">{movie.rating.toFixed(1)}</span>
                    分
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-fire-fill text-accent-400" aria-hidden="true"></i>
                    {formatPopularity(movie.popularity)} 次播放
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line" aria-hidden="true"></i>
                    {movie.updateDate} 更新
                  </span>
                </div>
                <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-6">
                  {movie.genres.map((genre) => (
                    <span
                      key={genre}
                      className="bg-background-50/15 text-background-100 text-xs font-medium px-3 py-1 rounded-full border border-background-50/20"
                    >
                      {genre}
                    </span>
                  ))}
                </div>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                  <a
                    href="#player"
                    className="flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-bold text-sm px-6 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                  >
                    <i className="ri-play-fill text-lg" aria-hidden="true"></i>
                    立即播放
                  </a>
                  <button
                    type="button"
                    onClick={() => setFavorited((prev) => !prev)}
                    className={`flex items-center gap-2 font-semibold text-sm px-6 py-3 rounded-md whitespace-nowrap cursor-pointer border transition-colors ${
                      favorited
                        ? "bg-accent-500/20 border-accent-400 text-accent-300"
                        : "bg-background-50/15 border-background-50/30 text-background-50 hover:bg-background-50/25"
                    }`}
                  >
                    <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true"></i>
                    {favorited ? "已收藏" : "收藏"}
                  </button>
                  <Link
                    to={`/category/${categoryKey}`}
                    className="flex items-center gap-2 bg-background-50/15 hover:bg-background-50/25 border border-background-50/30 text-background-50 font-semibold text-sm px-6 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                  >
                    <i className="ri-list-check-2 text-lg" aria-hidden="true"></i>
                    查看{movie.type}分类
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="px-4 md:px-6 py-8 md:py-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-2 space-y-8">
            <div id="player" className="scroll-mt-24">
              <h2 className="font-heading text-xl font-extrabold text-foreground-950 mb-4 flex items-center gap-2.5">
                <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
                在线播放
              </h2>
              <PlayerDemo movie={movie} />
            </div>

            <div>
              <h2 className="font-heading text-xl font-extrabold text-foreground-950 mb-4 flex items-center gap-2.5">
                <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
                剧情简介
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{movie.intro}</p>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="bg-background-50 rounded-lg border border-background-200 p-5 md:p-6">
              <h2 className="font-heading text-lg font-extrabold text-foreground-950 mb-4">
                作品信息
              </h2>
              <dl className="space-y-3.5">
                {metaItems.map((item) => (
                  <div key={item.label} className="flex gap-3 text-sm">
                    <dt className="shrink-0 w-16 text-foreground-400">{item.label}</dt>
                    <dd className="flex-1 text-foreground-800">{item.value}</dd>
                  </div>
                ))}
              </dl>
              <Link
                to={`/category/${categoryKey}`}
                className="mt-6 flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold py-2.5 rounded-md cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-arrow-right-up-line" aria-hidden="true"></i>
                更多{movie.type}推荐
              </Link>
            </div>
          </aside>
        </div>
      </div>

      <div className="bg-background-100">
        <div className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <SectionHeading
              title="相关推荐"
              subtitle={`喜欢《${movie.title}》的用户也在看`}
              moreLink={`/category/${categoryKey}`}
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}